import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import ReviewsSection from './components/ReviewsSection';
import RelatedProducts from './components/RelatedProducts';
import RecentlyViewed from './components/RecentlyViewed';

const ProductDetailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = parseInt(searchParams.get('id')) || 1;
  
  const [product, setProduct] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock product data
  const mockProducts = {
    1: {
      id: 1,
      name: "Organic Red Apples",
      brand: "Fresh Farms Organic",
      price: 4.99,
      originalPrice: 5.99,
      discount: 17,
      unit: "2 lbs",
      rating: 4.5,
      reviewCount: 128,
      inStock: true,
      stockCount: 45,
      images: [
        "https://images.unsplash.com/photo-1560806887-1e4cd0b6cbd6?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1619546813926-a78fa6372cd2?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1568702846914-96b305d2aaeb?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1576179635662-9d1983e97e1e?w=600&h=600&fit=crop"
      ],
      description: `These premium organic red apples are hand-picked at peak ripeness to ensure maximum flavor and nutritional value. Grown without synthetic pesticides or fertilizers, these apples offer a crisp texture and naturally sweet taste that's perfect for snacking, baking, or adding to your favorite recipes.\n\nOur organic farming practices not only produce superior tasting fruit but also support sustainable agriculture and environmental conservation. Each apple is carefully inspected for quality and freshness before packaging.`,
      features: [
        "100% Certified Organic",
        "Hand-picked at peak ripeness",
        "No synthetic pesticides or fertilizers",
        "Rich in fiber, vitamins, and antioxidants",
        "Perfect for snacking and cooking",
        "Sustainably grown and harvested"
      ],
      servingSize: "1 medium apple (182g)",
      nutrition: {
        calories: "95",
        totalFat: "0.3g",
        saturatedFat: "0.1g",
        cholesterol: "0mg",
        sodium: "2mg",
        totalCarbohydrates: "25g",
        dietaryFiber: "4g",
        sugars: "19g",
        protein: "0.5g",
        vitaminC: "14% DV",
        potassium: "6% DV"
      },
      ingredients: "Organic Red Apples",
      allergens: "This product is processed in a facility that also handles tree nuts and soy products.",
      storageInstructions: `Store in a cool, dry place or refrigerate to maintain freshness. For best quality, consume within 1-2 weeks of purchase. Wash thoroughly before eating.`,
      storageTemp: "32-40°F (0-4°C)",
      shelfLife: "2-3 weeks when refrigerated"
    },
    2: {
      id: 2,
      name: "Organic Bananas",
      brand: "Fresh Farms",
      price: 2.99,
      originalPrice: 3.49,
      discount: 14,
      unit: "1 lb",
      rating: 4.6,
      reviewCount: 89,
      inStock: true,
      stockCount: 32,
      images: [
        "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=600&h=600&fit=crop"
      ],
      description: `Premium organic bananas that are naturally sweet and perfectly ripe. Rich in potassium and essential nutrients.`,
      features: [
        "100% Certified Organic",
        "High in potassium",
        "Natural energy source",
        "Perfect for smoothies"
      ],
      servingSize: "1 medium banana (118g)",
      nutrition: {
        calories: "105",
        totalFat: "0.4g",
        saturatedFat: "0.1g",
        cholesterol: "0mg",
        sodium: "1mg",
        totalCarbohydrates: "27g",
        dietaryFiber: "3g",
        sugars: "14g",
        protein: "1.3g",
        vitaminC: "17% DV",
        potassium: "12% DV"
      },
      ingredients: "Organic Bananas",
      allergens: "None",
      storageInstructions: "Store at room temperature until ripe, then refrigerate if desired.",
      storageTemp: "Room temperature",
      shelfLife: "5-7 days"
    }
  };

  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      const foundProduct = mockProducts[productId];
      if (foundProduct) {
        setProduct(foundProduct);
        // Add to recently viewed
        addToRecentlyViewed(foundProduct);
      } else {
        navigate('/product-catalog-category-browse');
      }
      setLoading(false);
    }, 500);

    // Load wishlist from localStorage
    const savedWishlist = localStorage.getItem('wishlist');
    if (savedWishlist) {
      setWishlist(JSON.parse(savedWishlist));
    }
  }, [productId, navigate]);

  const addToRecentlyViewed = (product) => {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const filteredViewed = recentlyViewed.filter(item => item.id !== product.id);
    const updatedViewed = [product, ...filteredViewed].slice(0, 10); // Keep only last 10
    localStorage.setItem('recentlyViewed', JSON.stringify(updatedViewed));
  };

  const handleAddToCart = async (product, quantity) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = existingCart.find(item => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      existingCart.push({ ...product, quantity });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Dispatch custom event to update cart count
    window.dispatchEvent(new CustomEvent('cartUpdated'));
    
    // Simulate API call delay
    return new Promise(resolve => setTimeout(resolve, 800));
  };

  const handleAddToWishlist = (product) => {
    const isInWishlist = wishlist.some(item => item.id === product.id);
    let updatedWishlist;
    
    if (isInWishlist) {
      updatedWishlist = wishlist.filter(item => item.id !== product.id);
    } else {
      updatedWishlist = [...wishlist, product];
    }
    
    setWishlist(updatedWishlist);
    localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
  };

  const isInWishlist = product ? wishlist.some(item => item.id === product.id) : false;

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage-dashboard' },
    { label: 'Products', path: '/product-catalog-category-browse' },
    { label: product?.name || 'Product Details', path: `/product-detail-page?id=${productId}` }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse">
              <div className="h-4 bg-border rounded w-1/3 mb-6"></div>
              <div className="grid lg:grid-cols-2 gap-8">
                <div className="aspect-square bg-border rounded-card"></div>
                <div className="space-y-4">
                  <div className="h-8 bg-border rounded w-3/4"></div>
                  <div className="h-4 bg-border rounded w-1/2"></div>
                  <div className="h-6 bg-border rounded w-1/4"></div>
                  <div className="h-12 bg-border rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <h1 className="font-heading font-heading-bold text-2xl text-text-primary mb-4">
                Product Not Found
              </h1>
              <p className="text-text-secondary mb-6">
                The product you're looking for doesn't exist or has been removed.
              </p>
              <button
                onClick={() => navigate('/product-catalog-category-browse')}
                className="bg-primary text-primary-foreground px-6 py-3 rounded-input hover:bg-primary/90 transition-smooth"
              >
                Browse Products
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <Breadcrumb customItems={breadcrumbItems} />

          {/* Product Details */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
            {/* Product Images */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <ProductImageGallery 
                images={product.images} 
                productName={product.name} 
              />
            </div>

            {/* Product Information */}
            <div>
              <ProductInfo
                product={product}
                onAddToCart={handleAddToCart}
                onAddToWishlist={handleAddToWishlist}
                isInWishlist={isInWishlist}
              />
            </div>
          </div>

          {/* Product Tabs */}
          <div className="mb-12">
            <ProductTabs product={product} />
          </div>

          {/* Reviews Section */}
          <div className="mb-12">
            <ReviewsSection product={product} />
          </div>

          {/* Related Products */}
          <div className="mb-12">
            <RelatedProducts currentProductId={product.id} />
          </div>

          {/* Recently Viewed */}
          <div className="mb-8">
            <RecentlyViewed currentProductId={product.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;